/* eslint-disable @typescript-eslint/naming-convention */
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FieldValues, useForm, UseFormRegister } from 'react-hook-form';
import parsePath from 'src/lib/parse-path.js';

import {
  getCookie,
  getMsclkid,
  utmParametersToKeysMapping,
} from 'public/assets/js/analytics/cookie';
import { useFormOutageNotice } from 'src/contexts';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { useLDFlag } from 'src/contexts/LaunchDarklyContext/LaunchDarklyContext';
import { MARKETO_BASE_URL } from 'src/env';
import { mergeFilledMarketoVals } from 'src/lib/conversion/inferMarketoUtmFields';
import {
  MARKETO_CONVERSION_MIGRATION_FLAG,
  shouldDualSubmitConversion,
  shouldSubmitConversionOnly,
  submitConversionForm,
  submitConversionFormDirectly,
} from 'src/lib/conversion/submitConversionForm';
import useAdBlockDetector from 'src/hooks/useAdBlockDetector';
import useIntersection from 'src/hooks/useIntersection/useIntersection';
import useMarketo from 'src/hooks/useMarketo';
import { useRememberMarketoFormEmail } from 'src/hooks/useRememberMarketoFormEmail';
import useSegmentId from 'src/hooks/useSegmentId';
import Callout from 'threads5/Callout';

// helpers
import { ConsentCategory } from 'public/assets/js/analytics/utilities';
import { getActiveConsentCategories } from 'src/containers/ConsentManager/utils';
import {
  createRedactedUserProperties,
  generateUUID,
} from 'src/lib/analytics-utils';
import { FormTypes, isProduction, Locales } from 'src/lib/constants';
import {
  Events,
  FormDestinations,
  LINKEDIN_CAPI_EVENTS,
} from 'src/lib/trackingEvents';
import {
  appendSuccessParam,
  mapSearchParamsToObject,
  prependLocale,
  prependStagingHash,
} from 'src/lib/utils';
import { createDryRunMarketoForm } from './dryRunMarketoForm';
import { useTrackTextInputFocus } from './useTrackTextInputFocus';

export interface RenderProps {
  control: ReturnType<typeof useForm>['control'];
  errors: Record<string, unknown>;
  register: UseFormRegister<FieldValues>;
  setValue?: ReturnType<typeof useForm>['setValue'];
}
interface Props {
  redirectUrl?: string;
  marketoKey: number;
  onSubmit?: () => void;
  shouldForward?: boolean;
  render: (RenderProps) => React.ReactNode;
  trackingId?: string;
  locale?: string;
  formType?: FormTypes;
  rememberEmail?: boolean;
  dryRun?: boolean;
}

declare global {
  interface Window {
    Plaid: {
      getStoredUtmParams: () => Record<string, unknown>;
    };
    MktoForms2;
    qualified;
  }
}

// marketoForm Function
const MarketoForm: FC<Props> = ({
  redirectUrl,
  marketoKey,
  onSubmit = () => {
    return undefined;
  },
  shouldForward = true,
  render,
  trackingId = 'MARKETO_FORM',
  locale = '',
  formType = FormTypes.Legacy,
  rememberEmail = false,
  dryRun = false,
}) => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState,
    clearErrors,
    getValues,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const trackedSubmitCountRef = useRef(0);
  const submitInFlight = useRef(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const marketoFormLoaded = useRef(null);
  const track = useTrack();
  const router = useRouter();
  const basePath = router?.basePath || '';
  const currentPage = parsePath(router?.asPath).pathname;
  const conversionOnly =
    !dryRun && shouldSubmitConversionOnly(currentPage, marketoKey);
  useMarketo({
    baseUrl: MARKETO_BASE_URL,
    munchkinId: '495-WRE-561',
    formId: marketoKey,
    scriptLoaded,
    setScriptLoaded,
    marketoFormLoaded,
  });

  const { persistEmail } = useRememberMarketoFormEmail({
    enabled: rememberEmail,
    setValue,
    field: 'Email',
  });

  const hasAdBlocker = useAdBlockDetector();
  const segmentId = useSegmentId();
  const { formOutageNotice } = useFormOutageNotice();
  const formRef = useRef<HTMLFormElement>(null);
  const hasTrackedFormViewed = useRef(false);
  const hasTrackedFormStarted = useRef(false);
  const isFormInView = useIntersection({ element: formRef, useOnce: true });

  const getFunnelPayload = () => {
    return {
      trackingId,
      marketoKey,
      formType,
      locale,
    };
  };

  const trackFormStarted = () => {
    if (hasTrackedFormStarted.current) {
      return;
    }

    hasTrackedFormStarted.current = true;
    track({
      type: Events.FORM_STARTED,
      payload: getFunnelPayload(),
    });
  };

  const trackTextInputFocus = useTrackTextInputFocus({
    getPayload: getFunnelPayload,
  });

  const trackFieldFocus = (event: React.FocusEvent<HTMLFormElement>) => {
    trackFormStarted();
    trackTextInputFocus(event);
  };

  const trackSubmitError = (errorFields: string[]) => {
    track({
      type: Events.FORM_SUBMIT_ERROR,
      payload: {
        ...getFunnelPayload(),
        destination: FormDestinations.MARKETO,
        errorFields,
      },
    });
  };

  const conversionMigrationEnabled = useLDFlag(
    MARKETO_CONVERSION_MIGRATION_FLAG,
    false,
  );

  const sendSubmission = async (data) => {
    setIsSubmitting(true);

    const fromDemo = localStorage.getItem('from-demo') ?? '';

    const valObj = Object.assign({}, data, getSourceTrackingObj(), {
      hasAdBlocker,
      // Only include Segment ID when user has consented to Performance tracking
      // -- the same category that enables the Marketo Munchkin script.
      // @see src/containers/ConsentManager/ThirdPartyScripts.tsx
      segmentajsanonymousid:
        (getActiveConsentCategories().includes(ConsentCategory.PERFORMANCE)
          ? segmentId
          : null) ??
        // We've historically fallen back to empty quotes here; may be able to swap with
        // `null` but will need testing. It's possible this has real-world consequences.
        // @see https://experienceleaguecommunities.adobe.com/adobe-marketo-engage-27/tip-of-the-day-empty-string-vs-null-are-considered-different-inputs-in-adobe-marketo-engage-160847
        '',
      coastDemo: fromDemo,
    });

    const uuid = generateUUID();
    const redactedUserProperties = await createRedactedUserProperties({
      email: valObj?.Email,
    });

    const marketoFormReady = dryRun
      ? Promise.resolve(createDryRunMarketoForm())
      : marketoFormLoaded.current;

    const trackConversionResult = (ok: boolean) => {
      track({
        type: ok ? Events.FORM_SUBMIT_SUCCESS : Events.FORM_SUBMIT_ERROR,
        payload: {
          ...getFunnelPayload(),
          destination: FormDestinations.CONVERSION,
        },
      });
    };

    const submitToDestination = async (marketoForm?) => {
      if (conversionOnly) {
        // Submit without the consent-gated SDK so every visitor can get the report.
        const ok = await submitConversionForm(marketoKey, valObj, {
          submit: (formId, fields) => {
            return submitConversionFormDirectly(formId, fields, {
              includeAnalyticsIdentifiers:
                getActiveConsentCategories().includes(
                  ConsentCategory.PERFORMANCE,
                ),
            });
          },
        });
        trackConversionResult(ok);
        if (!ok) {
          submitInFlight.current = false;
          setIsSubmitting(false);
          return;
        }
      } else {
        // Marketo documentation for methods used: https://developers.marketo.com/javascript-api/forms/api-reference/
        marketoForm.vals(valObj);

        // Once we reach here our React form validation passed,
        // but it still needs to be checked if native Marketo form validation passed
        const nativeValidation = marketoForm.validate();
        if (!nativeValidation) {
          submitInFlight.current = false;
          setIsSubmitting(false);
          track({ type: `${trackingId}_NATIVE_VALIDATION_FAILED` });
          return;
        }

        marketoForm.submit();

        if (
          !dryRun &&
          shouldDualSubmitConversion(
            conversionMigrationEnabled,
            currentPage,
            marketoKey,
          )
        ) {
          void submitConversionForm(
            marketoKey,
            mergeFilledMarketoVals(valObj, marketoForm.vals()),
          ).then(trackConversionResult);
        }
      }

      if (!dryRun) {
        // LinkedIn CAPI analytics
        track({
          type: LINKEDIN_CAPI_EVENTS.FORM_SUBMIT,
          payload: {
            user_data: {
              linkedinFirstPartyId: window.li_fat_id,
              ...redactedUserProperties,
              address: {
                first_name: valObj?.FirstName,
                last_name: valObj?.LastName,
                country: valObj?.Country,
              },
              companyName: valObj?.Company,
            },
            currency: 'USD',
            value: '0',
            event_id: uuid,
          },
        });
      }

      const handleSuccess = (values, followUpUrl) => {
        persistEmail(valObj?.Email);

        if (window?.qualified && qualifiedMeetingForms?.[marketoKey]) {
          window?.qualified('saveFormData', {
            company: valObj?.Company,
            conversion_url: window?.location?.href,
            country: valObj.Country,
            email: valObj?.Email,
            first_name: valObj?.FirstName,
            landing_page_url: valObj?.referrerUrl,
            last_name: valObj?.LastName,
            phone: valObj?.Phone,
            state: valObj?.State,
            coast_demo: valObj?.coastDemo,
            utm_campaign: valObj?.['utm_campaign__c'],
            utm_content: valObj?.['utm_content__c'],
            utm_medium: valObj?.['utm_medium__c'],
            utm_source: valObj?.['utm_source__c'],
            utm_term: valObj?.['utm_term__c'],
            ///////////////////////////////////////
            account_owner: undefined,
            city: undefined,
            consumer: undefined,
            employee_count: valObj?.['EU_Employee_Estimate_Size__c'],
            employees_routing: undefined,
            experience_control_field: undefined,
            hq_country: undefined,
            lead_source: undefined,
            lead_source_detail: undefined,
            marketing_comms_optin: undefined,
            most_recent_cta: undefined,
            name: undefined,
            nba_notes: undefined,
            original_entry_url: undefined,
            owner_id: undefined,
            playback_url: undefined,
            revenue: undefined,
            status: undefined,
            stream_control_field: undefined,
            title: undefined,
          });
        }

        if (locale && locale !== Locales.EN_US) {
          const redirectUrl = followUpUrl.replace('https://plaid.com', '');
          followUpUrl = prependStagingHash(
            prependLocale({
              locale,
              href: redirectUrl,
            }),
            basePath,
          );
        }

        // Analytics tracking
        track(
          {
            type: `${trackingId}_SUCCESS`,
            payload: {
              ...redactedUserProperties,
              shouldForward,
              followUpUrl,
            },
          },
          // Pass redirect conditional as callback to track
          // to give browser time to make outbound requests first.
          // See our `track` impl in AnalyticsContext for details.
          () => {
            submitInFlight.current = false;
            setIsSubmitting(false);
            // we try submission logic from parent components
            // if tracking is disabled or blocked there is a try/catch
            // that will handle this callback no matter what happens
            onSubmit();

            // we do nothing if the form is supposed to stay on the page
            // this applies to all forms
            if (!shouldForward) {
              // trigger qualified experiance based on marketo key
              // this will trigger here if the page is not redirected or on the thank you page
              if (window?.qualified && qualifiedMeetingForms?.[marketoKey]) {
                window.qualified(
                  'showFormExperience',
                  qualifiedMeetingForms[marketoKey],
                );
              }
              return;
            }

            // we have to treat each type of form differently in order to
            // best support backwards compatibility
            switch (formType) {
              // legacy forms that should forward will always follow
              // the followUpUrl configured in Marketo
              case FormTypes.Legacy: {
                setRedirectLink(appendSuccessParam(followUpUrl, marketoKey));
                break;
              }
              // we first try to redirect to the marketo followUpUrl
              // otherwise we redirect to whatever is set in the cms button
              case FormTypes.CMS: {
                const { pathname } = parsePath(followUpUrl);
                if (followUpUrl && pathname !== currentPage) {
                  setRedirectLink(appendSuccessParam(followUpUrl, marketoKey));
                } else {
                  const goTo = appendSuccessParam(redirectUrl, marketoKey);
                  setRedirectLink(goTo);
                }
                break;
              }
              // we follow the redirect url for gated content forms
              // this wil be one of an asset or a button url as a fallback
              // it's unlikely the button url will be invoked
              case FormTypes.Gated: {
                setRedirectLink(appendSuccessParam(redirectUrl, marketoKey));
                break;
              }
            }
          },
        );

        return false;
      };

      if (conversionOnly) {
        // The report has no Marketo follow-up URL; forward to its CMS link.
        handleSuccess(valObj, redirectUrl ?? '');
      } else {
        marketoForm.onSuccess(handleSuccess);
      }
    };

    if (conversionOnly) {
      await submitToDestination();
    } else {
      await marketoFormReady.then(submitToDestination);
    }
  };

  const submitMarketo = async (data) => {
    // Most form templates leave the submit button enabled while a submit is
    // pending, and each extra click would create another Marketo submission.
    if (submitInFlight.current) {
      return;
    }
    submitInFlight.current = true;
    try {
      await sendSubmission(data);
    } catch (error) {
      // e.g. a click before forms2 has loaded the Marketo form. Release the
      // guard so the next click can retry.
      submitInFlight.current = false;
      setIsSubmitting(false);
      Sentry.captureException(error);
    }
  };

  // Fire SUBMIT once per submit attempt. The ref guard (vs. state) updates
  // synchronously, so effect re-runs and Strict Mode can't double-fire.
  useEffect(() => {
    const isNewSubmitAttempt =
      formState.isSubmitting &&
      trackedSubmitCountRef.current === formState.submitCount;

    if (isNewSubmitAttempt) {
      trackedSubmitCountRef.current += 1;
      track({ type: `${trackingId}_SUBMIT` });
    }
  }, [formState.isSubmitting, formState.submitCount, trackingId, track]);

  useEffect(() => {
    if (!isFormInView || hasTrackedFormViewed.current) {
      return;
    }

    hasTrackedFormViewed.current = true;
    track({
      type: Events.FORM_VIEWED,
      payload: getFunnelPayload(),
    });
    // `track` changes identity once ad block detection resolves, so the ref
    // guard keeps this to a single event. Payload is read at fire time so we
    // do not re-run this for flag or field changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormInView, track]);

  const onInvalidSubmit = (errors: Record<string, unknown>) => {
    trackSubmitError(Object.keys(errors));
  };

  return (
    <>
      <form
        ref={formRef}
        data-testid='marketo-form'
        data-marketo-key={marketoKey}
        onSubmit={handleSubmit(submitMarketo, onInvalidSubmit)}
        onFocusCapture={trackFieldFocus}
        onInput={trackFormStarted}
        noValidate
      >
        {formOutageNotice && isProduction && (
          <Callout
            description={formOutageNotice.description}
            status={formOutageNotice.status}
            title={formOutageNotice.title}
            sx={{ mb: 4 }}
          />
        )}
        {render({
          control,
          errors: formState.errors,
          formState,
          register,
          setValue,
          isSubmitting,
          clearErrors,
          getValues,
        })}
      </form>
    </>
  );
};

export default MarketoForm;

// 2006 and 2007 are contact us forms
// NOTE: Also update src/containers/ConsentManager/ThirdPartyScripts.tsx
const qualifiedMeetingForms = {
  2006: 'experience-1699026891029',
  2007: 'experience-1742313176070',
  2008: 'experience-1742313198160',
};

const getSourceTrackingObj = () => {
  const utmParams = global.window?.Plaid?.getStoredUtmParams();
  const urlParams = new URLSearchParams(window.location.search);
  const params = mapSearchParamsToObject(urlParams);
  const utmFallback = Object.keys(utmParametersToKeysMapping).reduce(
    (prev, curr) => {
      if (params[utmParametersToKeysMapping[curr]]) {
        prev[curr] = params[utmParametersToKeysMapping[curr]];
      }
      return prev;
    },
    {},
  );

  // @TODO: Reusing deprecated cookie.js method for now,
  // pending refactor of cookie logic.
  // [redacted]#############################
  const referrerUrl = getCookie('referrer_url');
  const referralURL = getCookie('referrer_url');
  const entryURL = getCookie('entry_url');
  const gclid = getCookie('gclid');

  return {
    ...utmParams,
    ...utmFallback,
    gclid__c: gclid,
    Microsoft_Lead_ID__c: getMsclkid(),
    referrerUrl,
    referralURL,
    entryURL,
    currentURL: window.location.href,
  };
};

const setRedirectLink = (redirectLink: string) => {
  if (redirectLink) {
    const link = document.createElement('a');
    link.href = redirectLink;
    link.click();
  } else {
    // eslint-disable-next-line no-console
    console.log('No redirect link found');
  }
};
